import React from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import {
    Typography,
    Button,
    Dialog,
    DialogContent,
    Stepper,
    Step,
    StepLabel,
    DialogActions,
    Grid,
    DialogTitle,
} from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { Loading } from "pages/parts/Loading";
import { ConfirmParts } from "pages/booking/dialogParts/ConfirmParts";
import { SubscribeParts } from "pages/booking/dialogParts/SubscribeParts";
import { EventType } from "pages/statuses/EventType";
import * as CommonConstants from "utilities/CommonConstants";
import "common.css";
import { SubscribeForm } from "pages/booking/dialogParts/SubscribeForm";
import { SelectBookingTime } from "pages/booking/dialogParts/SelectBookingTime";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: `100%`,
            position: `relative`,
        },
        instructions: {
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
        cancelButton: {
            marginLeft: theme.spacing(1),
        },
        loading: {
            width: `20%`,
            margin: `0 auto`,
            marginTop: 10,
            position: `absolute`,
            top: `50%`,
            left: `50%`,
        },
        closeButton: {
            position: `absolute`,
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500],
        },
        stepLabel: {
            fontSize: `x-small`,
        },
        errorMessage: {
            color: `#dc143c`,
            fontSize: `medium`,
        },
    })
);

interface Props {
    // 開くダイアログ選択
    isOpen: boolean;
    // キャンセル
    onClickCancel: VoidFunction;
    // 登録
    onClickAdd: VoidFunction;
    // 変更
    onClickChange: VoidFunction;
    // 予約情報
    booking: EventType;
    // イベント
    events: EventType[];
    // 次へ
    handleNext: VoidFunction;
    // 戻る
    handleBack: VoidFunction;
    // ステッパーの数値
    step: number;
    // 予約設定
    setNewBooking: any;
    // 研究室で予約済みの予約情報
    targetBookingForLaboratoryCheck: EventType;
    // 二重予約警告リンク
    onClickLinkDoubleBooking: VoidFunction;
    // エラーメッセージ
    errorMessage: string;
    // ローディング
    isLoading: boolean;
    // ローディング状態セッター
    setLoading: any;
}

const getSteps = () => {
    return [`時間選択`, `予約者情報`, `詳細情報`, `内容確認`];
};

/**
 * 予約登録ダイアログコンポーネント
 * 予約情報を登録するコンポーネント
 * @param props
 */
export const SubscribeDialog: React.FC<Props> = (props) => {
    const {
        isOpen,
        onClickCancel,
        onClickAdd,
        onClickChange,
        booking,
        events,
        handleNext,
        handleBack,
        step,
        setNewBooking,
        targetBookingForLaboratoryCheck,
        onClickLinkDoubleBooking,
        errorMessage,
        isLoading,
        setLoading,
    } = props;
    const classes = useStyles();
    const isPcSize = useMediaQuery(CommonConstants.RESPONSIVE_MIN_WIDTH);
    const steps = getSteps();

    const getStepContent = (stepIndex: number) => {
        switch (stepIndex) {
            case 0:
                return (
                    <SelectBookingTime
                        booking={booking}
                        events={events}
                        setNewBooking={setNewBooking}
                        setLoading={setLoading}
                        targetBookingForLaboratoryCheck={targetBookingForLaboratoryCheck}
                        onClickLinkDoubleBooking={onClickLinkDoubleBooking}
                    />
                );
            case 1:
                return <SubscribeParts booking={booking} setLoading={setLoading} />;
            case 2:
                return <SubscribeForm booking={booking} setLoading={setLoading} />;
            case 3:
                return <ConfirmParts booking={booking} setLoading={setLoading} />;
            default:
                return `Unknown stepIndex`;
        }
    };

    const checkStep = () => {
        const result = step === steps.length;
        return result;
    };

    // 登録・変更ボタンの判定
    const selectButton = () => {
        if (step === steps.length - 1) {
            return 0;
        }
        return 1;
    };

    // 次へボタンの判定(falseで非表示)
    const nextButton = () => {
        // 予約できる枠がない場合
        if (events.length === 0) {
            return false;
        }
        // 新規登録時、すでに同じ日時に予約済みの場合(二重予約)
        if (booking.id === `` && targetBookingForLaboratoryCheck.id !== ``) {
            return false;
        }

        // 表示する
        return true;
    };

    const body = (
        <>
            <IconButton aria-label="close" className={classes.closeButton} onClick={onClickCancel}>
                <CloseIcon />
            </IconButton>
            <DialogTitle id="form-dialog-title">
                <Stepper activeStep={step} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel classes={{ label: classes.stepLabel }}>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <p className={classes.errorMessage}>{errorMessage}</p>
            </DialogTitle>
            <DialogContent dividers>
                <div>
                    <Loading isLoading={isLoading} />
                    {checkStep() ? (
                        <div>
                            <Typography className={classes.instructions}>予約が完了しました。</Typography>
                        </div>
                    ) : (
                        <div className={classes.instructions}>{getStepContent(step)}</div>
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                {checkStep() ? (
                    <Button variant="contained" color="primary" onClick={onClickCancel}>
                        OK
                    </Button>
                ) : (
                    <Grid container>
                        <Grid item xs={9}>
                            <Button onClick={onClickCancel} className={classes.cancelButton} color="primary">
                                閉じる
                            </Button>
                        </Grid>
                        <Grid item xs={2}>
                            {((step === CommonConstants.WASTED_LIQUID_SUBSCRIBE_DIALOG && booking.id === ``) ||
                                step === CommonConstants.SUBSCRIBER_INFO_DIALOG ||
                                step === CommonConstants.BOOKING_CONFIRM_DIALOG) && (
                                <Button onClick={handleBack}>戻る</Button>
                            )}
                        </Grid>
                        <Grid item xs={1}>
                            {selectButton() === 0 ? (
                                <Grid item xs={1}>
                                    {booking.id === `` ? (
                                        <Button variant="contained" color="primary" onClick={onClickAdd}>
                                            登録
                                        </Button>
                                    ) : (
                                        <Button variant="contained" color="primary" onClick={onClickChange}>
                                            更新
                                        </Button>
                                    )}
                                </Grid>
                            ) : (
                                <>
                                    {nextButton() && (
                                        <Button variant="contained" color="primary" onClick={handleNext}>
                                            次へ
                                        </Button>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>
                )}
            </DialogActions>
        </>
    );

    return (
        <Dialog
            maxWidth="md"
            fullWidth={true}
            fullScreen={!isPcSize}
            open={isOpen}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            {body}
        </Dialog>
    );
};
